import classNames from 'classnames'
import { motion, useScroll } from 'framer-motion'
import { useRef } from 'react'
import { LOCALE_EN, LOCALE_KO, LOCALE_VI } from '../../../../../modules/i18n/config'
import { s_home } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import {
  IMG_HOME_DESKTOP_TUTOR_1,
  IMG_HOME_DESKTOP_TUTOR_2,
  IMG_HOME_DESKTOP_TUTOR_3,
  IMG_HOME_SECTION_ICON_TUTOR,
  IMG_HOME_V2_TUTOR_SECTION_IMAGE_1,
} from '../../../constants/home.constants'
import { HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA, HOME_V2_TUTOR_MARQUEE_TOP_DATA } from '../../../data/home.data'
import TutorUniversityList from './TutorUniversityList'

type TutorSectionV2Props = {
  bgColor: string
}

const TutorSectionV2: React.FC<TutorSectionV2Props> = ({ bgColor }) => {
  const { t, locale } = useTranslation()

  const sectionOneRef = useRef<HTMLDivElement>(null)
  const sectionTwoRef = useRef<HTMLDivElement>(null)
  const sectionThreeRef = useRef<HTMLDivElement>(null)
  const sectionFourRef = useRef<HTMLDivElement>(null)
  const sectionFiveRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress: scrollYSectionFive } = useScroll({
    target: sectionFiveRef,
    offset: ['start start', 'end end'],
  })

  return (
    <div style={{ backgroundColor: bgColor }} className="flex items-center justify-center pt-[119px]">
      <div className="flex max-w-[1080px] flex-col items-center justify-center px-[20px]">
        {/* ---------- Top Section ---------- */}
        <div className="w-full max-w-[689px] rounded-[16px] bg-white px-[25px] py-[60px]">
          {/* Label Section */}
          <div className="flex items-center justify-center">
            <img src={IMG_HOME_SECTION_ICON_TUTOR} alt="tutor-section-icon" className="h-[32px] w-[32px]" />
            <p className={classNames('ml-4 text-[#585D71]', locale === LOCALE_KO ? 'Headline3-22Bd' : 'Title1-20Bd')}>
              {t(s_home.tutor_title.subtitle.desktop)}
            </p>
          </div>

          {/* Title / Subtitle Section */}
          <div className="mt-16 text-center">
            <h2
              className={classNames(
                'whitespace-pre-line text-[#000000]',
                locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
              )}
            >
              {t(s_home.tutor_title.title.desktop)}
            </h2>

            <p
              className={classNames(
                'mt-16 whitespace-pre-line text-[#474B5B]',
                locale === LOCALE_KO ? 'Title1-20Md' : 'Title2-18Md'
              )}
            >
              {t(s_home.tutor_title.bodytxt.desktop)}
            </p>
          </div>

          {/* Tutor University Marquee Section */}
          <div className="mt-[54px]">
            <TutorUniversityList showTopList={true} showBottomList={false} />
          </div>
        </div>

        {/* ---------- Tutor Sub-Section 1 ---------- */}
        <div className="mt-[156px] flex w-full items-start justify-between pl-[68px] pr-[88px]" ref={sectionOneRef}>
          <div className="flex-1">
            <h2
              className={classNames(
                'whitespace-pre-line text-[#F8F8F8]',
                locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
              )}
            >
              {t(s_home.tutor_txt_docs.title_white.desktop)}
            </h2>
            <h2
              className={classNames(
                'mt-8 whitespace-pre-line text-[#CBC5F0]',
                locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
              )}
            >
              {t(s_home.tutor_txt_docs.title_purple.desktop)}
            </h2>
          </div>
          <div className="w-full max-w-[424px]">
            <p
              className={classNames(
                'whitespace-pre-line text-[#CBC5F0]',
                (locale === LOCALE_KO || locale === LOCALE_EN || locale === LOCALE_VI) && 'break-words break-keep',
                locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
              )}
            >
              {t(s_home.tutor_txt_docs.bodytxt.desktop)}
            </p>
            <img
              src={IMG_HOME_V2_TUTOR_SECTION_IMAGE_1}
              alt="tutor-sub-section-1-img"
              className="mt-[45px] h-full w-full"
            />
            <p
              className={classNames(
                'mt-16 text-center text-[#CDD0D9]',
                locale === LOCALE_KO ? 'Body1-16Md' : 'Body2-14Md'
              )}
            >
              {t(s_home.tutor_txt_docs.caption.desktop)}
            </p>
          </div>
        </div>

        {/* ---------- Tutor Sub-Section 2 ---------- */}
        <div className="mt-[200px] w-full bg-[#201852]" ref={sectionTwoRef}>
          <img src={IMG_HOME_DESKTOP_TUTOR_1(locale)} alt="tutor-image-section-2" className="h-full w-full" />

          <div className="mt-[160px] flex items-start justify-between pl-[68px] pr-[88px]">
            <div className="flex-1">
              <h2
                className={classNames(
                  'whitespace-pre-line text-[#F8F8F8]',
                  locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
                )}
              >
                {t(s_home.tutor_txt_tutorlist.title_white.desktop)}
              </h2>
              <h2
                className={classNames(
                  'mt-8 whitespace-pre-line text-[#CBC5F0]',
                  locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
                )}
              >
                {t(s_home.tutor_txt_tutorlist.title_purple.desktop)}
              </h2>
            </div>
            <div className="w-full max-w-[424px]">
              <p
                className={classNames(
                  'whitespace-pre-line text-[#CBC5F0]',
                  (locale === LOCALE_KO || locale === LOCALE_EN || locale === LOCALE_VI) && 'break-words break-keep',
                  locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
                )}
              >
                {t(s_home.tutor_txt_tutorlist.bodytxt_1.desktop)}
              </p>
              <p
                className={classNames(
                  'mt-12 break-words break-keep text-[#CBC5F0]',
                  locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
                )}
              >
                {t(s_home.tutor_txt_tutorlist.bodytxt_2.desktop)}
              </p>
            </div>
          </div>

          <div className="pl-[68px] pr-[88px]">
            <div className="relative w-full overflow-hidden pt-[32px]">
              <div className="marquee_anim_v2 top relative mb-16 flex items-center">
                <div className="marquee_item flex items-center justify-start space-x-[12px]">
                  {[
                    ...HOME_V2_TUTOR_MARQUEE_TOP_DATA(locale),
                    ...HOME_V2_TUTOR_MARQUEE_TOP_DATA(locale),
                    ...HOME_V2_TUTOR_MARQUEE_TOP_DATA(locale),
                    ...HOME_V2_TUTOR_MARQUEE_TOP_DATA(locale),
                    ...HOME_V2_TUTOR_MARQUEE_TOP_DATA(locale),
                  ].map((data, i) => (
                    <div
                      key={`str-${i}`}
                      className={classNames(
                        'whitespace-pre-line rounded-[4px] px-10 py-8 text-center',
                        locale === LOCALE_KO ? 'Title1-20Bd' : 'Title2-18Bd'
                      )}
                      style={{ backgroundColor: data.bgColor }}
                    >
                      <span style={{ color: data.textColor }}>{data.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="marquee_anim_v2 bottom relative flex items-center">
                <div className="marquee_item flex items-center justify-start space-x-[12px]">
                  {[
                    ...HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA,
                    ...HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA,
                    ...HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA,
                    ...HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA,
                    ...HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA,
                    ...HOME_V2_TUTOR_MARQUEE_BOTTOM_DATA,
                  ].map((data, i) => (
                    <div
                      key={`str-${i}`}
                      className={classNames(
                        'whitespace-pre-line rounded-[4px] px-12 py-8 text-center',
                        locale === LOCALE_KO ? 'Title1-20Bd' : 'Title2-18Bd'
                      )}
                      style={{ backgroundColor: data.bgColor }}
                    >
                      <span style={{ color: data.textColor }}>{data.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tutor Sub-Section 3 */}
        <div ref={sectionThreeRef} className="w-full pt-[140px]">
          <img
            src={IMG_HOME_DESKTOP_TUTOR_2(locale)}
            alt="tutor-image-section-3"
            loading="lazy"
            className="h-full w-full"
          />

          <div className="mt-[160px] flex items-start justify-between space-x-[28px] pl-[68px] pr-[88px]">
            <div className="flex-1">
              <h2
                className={classNames(
                  'whitespace-pre-line text-[#F8F8F8]',
                  locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
                )}
              >
                {t(s_home.tutor_txt_ivyleague.title_white.desktop)}
              </h2>
              <h2
                className={classNames(
                  'mt-8 whitespace-pre-line text-[#CBC5F0]',
                  locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
                )}
              >
                {t(s_home.tutor_txt_ivyleague.title_purple.desktop)}
              </h2>
            </div>

            <div className="w-full max-w-[424px]">
              <p
                className={classNames(
                  'whitespace-pre-line text-[#CBC5F0]',
                  (locale === LOCALE_KO || locale === LOCALE_EN || locale === LOCALE_VI) && 'break-words break-keep',
                  locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
                )}
              >
                {t(s_home.tutor_txt_ivyleague.bodytxt.desktop)}
              </p>
            </div>
          </div>
        </div>

        {/* Tutor Sub-Section 4 */}
        <div className="w-full pt-[200px]" ref={sectionFourRef}>
          <img
            src={IMG_HOME_DESKTOP_TUTOR_3(locale)}
            alt="tutor-image-section-4"
            className="h-full w-full"
            loading="lazy"
          />

          <div className="mt-[160px] flex items-start justify-center pl-[68px] pr-[88px]">
            <div className="flex-1 space-y-8">
              <h2
                className={classNames(
                  'whitespace-pre-line text-[#F8F8F8]',
                  locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
                )}
              >
                {t(s_home.tutor_txt_business.title_white.desktop)}
              </h2>
              <h2
                className={classNames(
                  'whitespace-pre-line text-[#CBC5F0]',
                  locale === LOCALE_KO ? 'Hero3-36Bd' : 'Hero4-32Bd'
                )}
              >
                {t(s_home.tutor_txt_business.title_purple.desktop)}
              </h2>
            </div>
            <div className="w-full max-w-[424px] space-y-12">
              <p
                className={classNames(
                  'whitespace-pre-line text-[#CBC5F0]',
                  (locale === LOCALE_KO || locale === LOCALE_EN || locale === LOCALE_VI) && 'break-words break-keep',
                  locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
                )}
              >
                {t(s_home.tutor_txt_business.bodytxt_1.desktop)}
              </p>
              <p
                className={classNames(
                  'whitespace-pre-line text-[#CBC5F0]',
                  (locale === LOCALE_KO || locale === LOCALE_EN || locale === LOCALE_VI) && 'break-words break-keep',
                  locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
                )}
              >
                {t(s_home.tutor_txt_business.bodytxt_2.desktop)}
              </p>
              <p
                className={classNames(
                  'whitespace-pre-line text-[#CBC5F0]',
                  (locale === LOCALE_KO || locale === LOCALE_EN || locale === LOCALE_VI) && 'break-words break-keep',
                  locale === LOCALE_KO ? 'Headline3-22Md' : 'Title1-20Md'
                )}
              >
                {t(s_home.tutor_txt_business.bodytxt_3.desktop)}
              </p>
            </div>
          </div>
        </div>

        {/* Tutor Sub-Section 5 */}
        <div className="h-[2400px] w-full bg-[#201852]" ref={sectionFiveRef}>
          <div className="sticky top-1/2 flex -translate-y-1/2 items-center justify-center">
            <motion.p
              className={classNames(
                'whitespace-pre-line text-center text-[#F8F8FB]',
                locale === LOCALE_KO ? 'Hero1-60Bd' : 'Hero2-48Bd'
              )}
              style={{ opacity: scrollYSectionFive }}
            >
              {t(s_home.tutor_txt_transit.desktop)}
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TutorSectionV2
